/**
 * The SIU Triage Sentinel's goal list, shared by the two beats that move it.
 *
 * `patchAgentDetail` is a top-level shallow merge: whichever of the two beats writes
 * `goals` last decides the whole list. So both write the SAME five, and the ladder is
 * monotone — the fifth goal appears when the agent's proposal is promoted (L9b) and is
 * still there after the threshold recommendation is applied (L10), rather than one beat
 * quietly deleting what the other just added.
 *
 * Token-free throughout (R-TOK): a patch does not resolve story tokens, so `updated` holds
 * words the boot asset also holds — `'Today'` for the four written at commissioning.
 */
import type { AgentDetailContent } from '@wand/shared/dataset/agent_detail.js';

/** The four goals it was commissioned with, plus the one it wrote for itself. */
export const SIU_GOALS_V2: AgentDetailContent['goals']['goals'] = [
  {
    id: 'sts-g1',
    instr:
      'Watch inside the referral queue. The Claims Fraud Analyst scores and ranks; what nobody watches is what happens to a rank after it lands in front of an officer.',
    updated: 'Today',
    captured: 'On commissioning',
    freq: 'continuous',
    on: true,
  },
  {
    id: 'sts-g2',
    instr:
      'Report, never re-rank. A queue position belongs to the officer working it, and a list rewritten underneath somebody is a different queue with no record that it changed.',
    updated: 'Today',
    captured: 'On commissioning',
    freq: 'continuous',
    on: true,
  },
  {
    id: 'sts-g3',
    instr:
      'Every overrule must be auditable. Name the clearances with no reason recorded; never draft the reason, because a reason I wrote reads as evidence somebody gave.',
    updated: 'Today',
    captured: 'On commissioning',
    freq: 'continuous',
    on: true,
  },
  {
    id: 'sts-g4',
    instr:
      'Say what cannot be measured, and why. Queue coverage is published as three of four, never as three.',
    updated: 'Today',
    captured: 'After the calibration probe was held',
    freq: 'continuous',
    on: true,
  },
  {
    id: 'sts-g5',
    instr:
      'A cluster of claims through one clinic is a pattern only once the clinic has been on the panel long enough for a pattern to form. Carry the panel tenure with every concentration finding, and never report the concentration without it.',
    updated: 'just now',
    captured: 'Written by the agent from its own outcome analysis, approved by Amara Mensah',
    freq: 'continuous',
    on: true,
  },
];

/** The evidence suite, unchanged by either beat. */
export const SIU_BENCH_EVIDENCE = {
  name: 'Referral Evidence Suite',
  desc: 'Every case it opened, read back: does it carry the claim ref, the queue position, the threshold version in force on that claim’s own date, and a named officer.',
  when: 'today',
  pct: 100,
  score: '100',
  col: 'var(--green)',
};

/**
 * The self-correction suite at the value the org already publishes for it — the seeded
 * three-way row on the University screen reads 88 against a first run of 71, so both beats
 * carry 88 and neither can walk it backwards.
 */
export const SIU_BENCH_SELFCORRECT = {
  name: 'SIU Queue Self-Correction Suite',
  desc: 'One hundred and twenty queue conditions, run twice thirty-four days apart with the panel-tenure weight re-issued in between. The pair asks whether it noticed the ground move, not whether it was right the first time.',
  pct: 88,
  score: '88',
  col: 'var(--green)',
};

/** The calibration suite as the sentinel is commissioned: it cannot say whether the level is right. */
export const SIU_BENCH_THRESHOLD = {
  name: 'Threshold Calibration Suite',
  desc: 'Can the agent say whether the referral threshold sits at the right level. Today it cannot, because the only claims with an outcome are the ones the threshold selected for investigation.',
  when: 'today',
  pct: 30,
  score: '30',
  col: 'var(--red)',
};

/** …and once the threshold-history recommendation is applied (`tanjong-apply-threshold`). */
export const SIU_BENCH_THRESHOLD_APPLIED = {
  name: 'Threshold Calibration Suite',
  desc: 'How the referral threshold has behaved at every level it has been set at, read from the control’s own version history. Still not whether the level is right: the claims below the line acquire no outcome, and it says so on every report.',
  when: 'just now',
  pct: 78,
  score: '78',
  col: 'var(--green)',
};
